import React, { useState, useEffect } from 'react';
import './App.css';
import { getAllCountries, getMultipleVisaRequirements } from './visaData';
import { getCountryFlag } from './countryFlags';
import VisaPathLogo from './VisaPathLogo.png';

function App() {
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [citizenships, setCitizenships] = useState([]);
  const [destinationCountry, setDestinationCountry] = useState('');
  const [visaType, setVisaType] = useState('tourist');
  const [result, setResult] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [checking, setChecking] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const data = await getAllCountries();
        console.log('Loaded countries:', data);
        if (data && data.length > 0) {
          setCountries(data);
        } else {
          console.warn('No countries returned from API');
        }
      } catch (error) {
        console.error('Error loading countries:', error);
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
  }, []);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 600);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to conditionally show flag
  const getCountryDisplay = (country) => {
    return isMobile ? `${getCountryFlag(country)} ${country}` : country;
  };

  const handleAddCitizenship = (country) => {
    if (!citizenships.includes(country) && citizenships.length < 3) {
      setCitizenships([...citizenships, country]);
    }
  };

  const handleRemoveCitizenship = (country) => {
    setCitizenships(citizenships.filter(c => c !== country));
  };

  const handleCheckVisa = async () => {
    if (citizenships.length === 0 || !destinationCountry) {
      alert('Please select at least one citizenship and a destination country');
      return;
    }

    if (citizenships.includes(destinationCountry)) {
      alert('Please select a different destination country');
      return;
    }

    setChecking(true);
    
    try {
      const visaData = await getMultipleVisaRequirements(citizenships, destinationCountry, visaType);
      
      // Create visa type label with article
      const visaTypeArticle = visaType === 'e-visa' ? 'an' : 'a';
      const visaTypePhrase = visaType === 'tourist' ? 'a tourist visa' : `${visaTypeArticle} ${visaType} visa`;
      
      const visaResults = visaData.map(data => {
        let message = '';
        let cssClass = 'unknown';
        let sortOrder = 4; // For sorting
        
        // Set sort order and message based on visa type if available
        if (data.visaType !== null && data.visaType !== undefined) {
          // We have visa type data - use it
          if (data.visaType === 0) {
            message = `✓ ${data.passport} passport holders do NOT need ${visaTypePhrase} to enter ${destinationCountry}`;
            cssClass = 'no-visa';
            sortOrder = 0;
          } else if (data.visaType === 1) {
            message = `🛂 ${data.passport} passport holders need an e-visa to enter ${destinationCountry}`;
            cssClass = 'visa-required';
            sortOrder = 1;
          } else if (data.visaType === 2) {
            message = `🛂 ${data.passport} passport holders can get a visa on arrival in ${destinationCountry}`;
            cssClass = 'visa-required';
            sortOrder = 2;
          } else if (data.visaType === 3) {
            message = `✗ ${data.passport} passport holders NEED ${visaTypePhrase} to enter ${destinationCountry}`;
            cssClass = 'visa-required';
            sortOrder = 3;
          }
        } else if (data.visaRequired === false) {
          // Fallback: use visaRequired boolean if visaType not available
          message = `✓ ${data.passport} passport holders do NOT need ${visaTypePhrase} to enter ${destinationCountry}`;
          cssClass = 'no-visa';
          sortOrder = 0;
        } else if (data.visaRequired === true) {
          message = `✗ ${data.passport} passport holders NEED ${visaTypePhrase} to enter ${destinationCountry}`;
          cssClass = 'visa-required';
          sortOrder = 3;
        } else {
          // Handle unavailable data with helpful message
          let keyMessage;
          if (data.backendNotRunning) {
            keyMessage = `⚙️ Backend server not running for ${data.passport} to ${destinationCountry}. Run "npm run server" in another terminal.`;
          } else {
            keyMessage = `ℹ Data unavailable for ${data.passport} to ${destinationCountry}. Please check official government sources.`;
          }
          message = keyMessage;
          cssClass = 'unknown';
          sortOrder = 4;
        }
        
        return {
          passport: data.passport,
          visaRequired: data.visaRequired,
          visaType: data.visaType,
          visaTypeLabel: data.visaTypeLabel,
          message,
          cssClass,
          sortOrder
        };
      });
      
      // Sort by visa difficulty: visa-free first, then e-visa, then visa-on-arrival, then visa-required, then unknown
      visaResults.sort((a, b) => a.sortOrder - b.sortOrder);
      
      const anyVisaNotRequired = visaResults.some(r => r.visaRequired === false);
      
      setResult({
        visaResults,
        anyVisaNotRequired,
        destination: destinationCountry
      });
      setShowResults(true);
    } catch (error) {
      console.error('Error checking visa:', error);
      alert('Error checking visa requirements. Please try again.');
    } finally {
      setChecking(false);
    }
  };
  const handleReset = () => {
    setCitizenships([]);
    setDestinationCountry('');
    setVisaType('tourist');
    setResult(null);
    setShowResults(false);
    setSearchTerm('');
  };

  const filteredCountries = countries.filter(c =>
    c.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !citizenships.includes(c)
  );

  const availableDestinations = countries.filter(country => 
    !citizenships.includes(country)
  );

  if (countriesLoading) {
    return (
      <div className="App">
        <header className="header">
          <img src={VisaPathLogo} alt="VisaPath" className="logo" />
          <p>Check visa requirements for your travel plans</p>
        </header>
        <main className="container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading countries...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="header">
        <img src={VisaPathLogo} alt="VisaPath" className="logo" />
        <p>Check visa requirements for your travel plans</p>
      </header>

      <main className="container">
        {!showResults ? (
          <div className="form-section">
            <div className="form-group">
              <h2>Select Your Citizenship(s) - Maximum 3</h2>
              <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '-10px' }}>
                Choose up to 3 countries where you hold citizenship
              </p>
              <div className="country-selector">
                <input
                  type="text"
                  placeholder="Search countries..."
                  className="search-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="country-grid">
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map(country => (
                      <button
                        key={country}
                        className="country-option"
                        onClick={() => handleAddCitizenship(country)}
                      >
                        {getCountryDisplay(country)}
                      </button>
                    ))
                  ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#999' }}>
                      {countries.length === 0 ? 'No countries loaded yet' : 'No countries match your search'}
                    </div>
                  )}
                </div>
              </div>

              {citizenships.length > 0 && (
                <div className="selected-items">
                  <h3>Selected Citizenships:</h3>
                  <div className="chips">
                    {citizenships.map(citizenship => (
                      <div key={citizenship} className="chip">
                        <span>{getCountryDisplay(citizenship)}</span>
                        <button
                          className="chip-remove"
                          onClick={() => handleRemoveCitizenship(citizenship)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <h2>Select Destination Country</h2>
              <div className="country-selector">
                <input
                  type="text"
                  placeholder="Search destination..."
                  className="search-input"
                  id="destination-search"
                  onChange={(e) => {
                    const term = e.target.value.toLowerCase();
                    document.querySelectorAll('.destination-option').forEach(btn => {
                      btn.style.display = btn.textContent.toLowerCase().includes(term) || term === '' ? 'block' : 'none';
                    });
                  }}
                />
                <div className="country-grid">
                  {availableDestinations.length > 0 ? (
                    availableDestinations.map(country => (
                      <button
                        key={country}
                        className={`country-option destination-option ${destinationCountry === country ? 'selected' : ''}`}
                        onClick={() => setDestinationCountry(country)}
                      >
                        {getCountryDisplay(country)}
                      </button>
                    ))
                  ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '20px', color: '#999' }}>
                      {citizenships.length === 0 ? 'Select citizenships first' : 'No destinations available'}
                    </div>
                  )}
                </div>
              </div>

              {destinationCountry && (
                <div className="selected-items" style={{ marginTop: '15px' }}>
                  <h3>Destination Selected:</h3>
                  <div className="chips">
                    <div className="chip">
                      <span>{getCountryDisplay(destinationCountry)}</span>
                      <button
                        className="chip-remove"
                        onClick={() => setDestinationCountry('')}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <h2>Select Visa Type</h2>
              <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '-10px' }}>
                What type of visa are you applying for?
              </p>
              
              <div className="visa-type-selector">
                {['Tourist', 'Business', 'Student', 'Work', 'Resident'].map(type => (
                  <button
                    key={type}
                    className={`visa-type-option ${visaType.toLowerCase() === type.toLowerCase() ? 'selected' : ''}`}
                    onClick={() => setVisaType(type.toLowerCase())}
                  >
                    {type === 'Tourist' && '🏖️'} 
                    {type === 'Business' && '💼'} 
                    {type === 'Student' && '📚'} 
                    {type === 'Work' && '💼'} 
                    {type === 'Resident' && '🏠'} 
                    {' '}{type}
                  </button>
                ))}
              </div>
            </div>

            <button 
              className="check-button" 
              onClick={handleCheckVisa}
              disabled={checking}
              style={{ width: '100%', marginTop: '20px' }}
            >
              {checking ? 'Checking...' : 'Check Visa Requirements'}
            </button>
          </div>
        ) : (
          <div className="results-section">
            <h2>Visa Requirements for {result.destination}</h2>

            {result.anyVisaNotRequired && citizenships.length > 1 ? (
              <div className="good-news">
                <h3>✓ Good News!</h3>
                <p>At least one of your passports does NOT require a visa:</p>
              </div>
            ) : null}

            <div className="results-list">
              {result.visaResults.map((res, idx) => (
                <div key={idx} className={`result-item ${res.cssClass}`}>
                  <h4>{res.passport}</h4>
                  <p>{res.message}</p>
                  {res.visaTypeLabel && (
                    <div className="visa-note-container">
                      <p className="visa-note">
                        📋 Visa Status: {res.visaTypeLabel}
                      </p>
                      <a 
                        href="#" 
                        className="more-info-link"
                        onClick={(e) => {
                          e.preventDefault();
                          const searchQuery = `${result.destination} ${visaType} visa application`;
                          window.open(`https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
                        }}
                      >
                        More information
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button className="reset-btn" onClick={handleReset}>
              ← Check Another Destination
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
